// src/stores/useSiteAgentStore.js
//
// SiteAgent's state: which pad is selected, what is on it, and which group the
// person has tapped.
//
// ── WHY A STORE AND NOT useState ──────────────────────────────────────────
//
// The same reason useRoutingStore exists: Starlink drops mid-use. A pad's
// assets are one request, and having them survive a component remount means a
// rep who backgrounds the app at a wellhead and comes back still has the list.
// Nothing here is persisted to localStorage — it is per-session, because a
// stale asset list presented as current is worse than an empty one.
//
// ── THE UNLICENSED STATE IS CARRIED, NOT FILTERED ─────────────────────────
//
// `categories` comes back from the API with every category present, including
// the ones this org does not hold — those carry `granted:false` and
// `asset_count:null`. This store does NOT drop them. A view that only ever
// receives what it may see cannot tell the person why something is missing,
// and "missing because empty" and "missing because unlicensed" are the two
// answers a field rep most needs to distinguish. REM-278 is the register's
// precedent for what happens when distinct refusals collapse into one.

import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";

export const useSiteAgentStore = create((set, get) => ({
  padId: null,
  pad: null,
  categories: [],
  totals: null,
  imageryDates: [],
  radiusM: 50,

  loading: false,
  error: null,
  // Set when the endpoint refuses the capability, mirroring
  // useRoutingStore.notEntitled: the FieldPage card reads it so the first
  // refusal teaches the shell rather than sending someone back into it.
  notEntitled: false,

  selectedCategory: null,
  selectedClassKey: null,

  clear: () => set({
    padId: null, pad: null, categories: [], totals: null, imageryDates: [],
    error: null, selectedCategory: null, selectedClassKey: null,
  }),

  selectGroup: (categoryKey, classKey) => set((s) =>
    s.selectedCategory === categoryKey && s.selectedClassKey === classKey
      ? { selectedCategory: null, selectedClassKey: null }
      : { selectedCategory: categoryKey, selectedClassKey: classKey }
  ),

  loadPad: async (observationId) => {
    if (!observationId) return;
    set({
      loading: true, error: null, padId: observationId,
      selectedCategory: null, selectedClassKey: null,
    });
    try {
      const res = await fetchWithAuth(
        `/api/v1/field/pads/${encodeURIComponent(observationId)}/assets`
      );

      if (res.status === 403) {
        // Same branch useRoutingStore keeps for /route. The body carries a
        // `code` — license_assignment_missing, feature_entitlement_missing,
        // license_capability_denied — and they are different problems, so the
        // reason is kept rather than flattened to a boolean.
        let code = null;
        try { code = (await res.json())?.code ?? null; } catch { /* no body */ }
        set({
          loading: false, notEntitled: true, pad: null, categories: [],
          error: code === "feature_entitlement_missing"
            ? "This organization is not licensed for the layers SiteAgent reads."
            : "SiteAgent is not included in your subscription.",
        });
        return;
      }

      if (res.status === 404) {
        // The endpoint deliberately does not distinguish "no such pad", "not a
        // pad" and "not visible to you" — telling an unentitled caller that an
        // id exists leaks what the grant withholds. So the copy cannot claim
        // which it was either.
        set({
          loading: false, pad: null, categories: [], totals: null,
          error: "That pad is not available to this organization.",
        });
        return;
      }

      if (!res.ok) throw new Error(`site_assets_${res.status}`);

      const d = await res.json();
      set({
        loading: false,
        pad: d.pad,
        categories: d.categories ?? [],
        totals: d.totals ?? null,
        imageryDates: d.imagery_dates ?? [],
        radiusM: d.radius_m ?? 50,
        error: null,
      });
    } catch (e) {
      set({ loading: false, error: String(e?.message || e) });
    }
  },

  /**
   * Every asset as a FeatureCollection for the map, each feature carrying its
   * group so a tap on a group can drive a filter rather than a second source.
   */
  assetsAsFeatures: () => {
    const features = [];
    for (const c of get().categories) {
      for (const g of c.groups ?? []) {
        for (const a of g.assets ?? []) {
          if (!a.geometry) continue;
          features.push({
            type: "Feature",
            geometry: a.geometry,
            properties: {
              id: a.id,
              category: c.key,
              class_key: a.class_key,
              inside: a.inside === true,
            },
          });
        }
      }
    }
    return { type: "FeatureCollection", features };
  },
}));
